import constants from '#packages/constants';
import { events } from '#packages/coreBi';
import { utils as coreUtils } from '#packages/core';
import { fedopsLogger, sections as sectionsUtils } from '#packages/util';

import {
  cleanupEditorData,
  debugReporter,
  errorReporter,
  getADIMigrationTrigger,
  isTimeLimitError,
  isPartialMigrationError,
  isMigrationCancelled,
  isSaveError,
  shouldUpdateMigrationVersionAfterFailure,
  isForceSectionsMigration,
  renameRevision,
  removeForceSectionsMigrationQuery,
  hasPagesWithSectionsInRoot,
  getFedopsInteractionKey,
  hasPagesWithoutSectionsInRoot,
  getPagesVisibleInMenu,
} from '../utils';
import { MigrationFlow } from '../types';
import { MIGRATION_VERSION } from '../version';
import {
  BEFORE_SECTIONS_MIGRATION_HISTORY_LABEL,
  DEBUG,
  ADIMigrationTrigger,
  MAX_PAGES_TO_MIGRATE,
} from '../constants';
import { saveAfterMigration, saveInBackground } from './saveMigration';
import { reportMigrationNotPossible } from './reportMigrationNotPossible';
import { restoreToADI } from './restoreToADI';
import { restoreSession } from './restoreSession';
import { startTourOrShowNotification } from './startTourOrShowNotification';
import { revertMigrationTransaction } from './revertMigrationTransaction';
import { showErrorNotification } from './showNotification';

import type { CompRef } from 'types/documentServices';
import type { SectionsMigrationScope as Scope } from '../scope';
import type { MigrationOrigin } from '../constants';

export async function afterSuccessMigration(
  scope: Scope,
  {
    flow,
    origin,
    startTime,
    pagesRefs,
  }: {
    flow: MigrationFlow;
    origin: MigrationOrigin;
    startTime: number;
    pagesRefs: CompRef[];
  },
) {
  const shouldRenameRevision = isForceSectionsMigration();

  if (shouldRenameRevision) {
    renameRevision(
      scope,
      'FTE_Tour_partners_update_site_history_pre_update_label',
    );
  }

  const shouldRunAsyncSave =
    flow === MigrationFlow.HeavySite && pagesRefs.length > MAX_PAGES_TO_MIGRATE;

  if (shouldRunAsyncSave) {
    sectionsUtils.updateSiteSectionsEditorData(scope.editorAPI, {
      isSectionsEnabled: true,
      isMigratedFromAdi: false,
      migrationVersion: MIGRATION_VERSION,
    });

    await saveAfterMigration(scope);
  }

  const duration = Math.round(performance.now() - startTime);
  debugReporter(scope, DEBUG.LOG, 'Migration time:', duration);

  scope.editorAPI.bi.event(events.sectionsMigration.MIGRATION_SUCCESS, {
    flow,
    origin,
    duration,
    is_document_changed: true,
    pages_list: pagesRefs.map(({ id }) => id).join(','),
    is_home_page:
      flow === MigrationFlow.PerPageEditor || flow === MigrationFlow.PerPageADI
        ? pagesRefs[0].id === scope.editorAPI.homePage.get()
        : null,
    template_id:
      scope.editorAPI.documentServices.generalInfo.getSiteOriginalTemplateId(),

    fully_migrated:
      flow === MigrationFlow.PerPageEditor || flow === MigrationFlow.PerPageADI
        ? !hasPagesWithoutSectionsInRoot(
            scope.editorAPI.documentServices,
            getPagesVisibleInMenu(scope.editorAPI.documentServices),
          )
        : null,
    page_count: scope.editorAPI.pages.getPagesList().length,
  });

  if (flow !== MigrationFlow.Editor2Anchors) {
    sectionsUtils.updateSiteSectionsEditorData(scope.editorAPI, {
      isSectionsEnabled: true,
      isMigratedFromAdi:
        flow === MigrationFlow.ADI || flow === MigrationFlow.PerPageADI,
      migrationVersion: MIGRATION_VERSION,
    });
  }

  if (
    flow === MigrationFlow.PerPageADI ||
    flow === MigrationFlow.PerPageEditor
  ) {
    sectionsUtils.updatePageSectionsEditorData(
      scope.editorAPI,
      {
        isSectionsEnabled: true,
        isMigratedFromAdi: flow === MigrationFlow.PerPageADI,
        migrationVersion: MIGRATION_VERSION,
      },
      pagesRefs,
    );
  }

  scope.editorAPI.history.amend();

  const FEDOPS_INTERACTION_KEY = getFedopsInteractionKey(flow);

  fedopsLogger.interactionEnded(FEDOPS_INTERACTION_KEY);

  startTourOrShowNotification(scope, flow, pagesRefs);

  finishMigration(scope, {
    flow,
    shouldRenameRevision,
  });
}

export async function afterFailedMigration(
  scope: Scope,
  {
    error,
    flow,
    origin,
    pagesRefs,
    initialHistoryLabel,
  }: {
    error: any;
    flow: MigrationFlow;
    origin: MigrationOrigin;
    pagesRefs: CompRef[];
    initialHistoryLabel: string;
  },
) {
  const { documentServices } = scope.editorAPI;

  const { undoLabelBeforeRevert, undoLabelAfterRevert } =
    await revertMigrationTransaction(scope);

  if (
    shouldUpdateMigrationVersionAfterFailure(scope, {
      error,
      flow,
    })
  ) {
    sectionsUtils.updateSiteSectionsEditorData(scope.editorAPI, {
      isSectionsEnabled: false,
      migrationVersion: MIGRATION_VERSION,
    });
  }

  scope.editorAPI.bi.event(events.sectionsMigration.MIGRATION_ERROR, {
    errorMessage: `Migration error ${error.message}`,
    flow,
    origin,
    template_id:
      scope.editorAPI.documentServices.generalInfo.getSiteOriginalTemplateId(),
    page_id:
      flow === MigrationFlow.PerPageEditor || flow === MigrationFlow.PerPageADI
        ? pagesRefs[0].id
        : null,
    is_home_page:
      flow === MigrationFlow.PerPageEditor || flow === MigrationFlow.PerPageADI
        ? pagesRefs[0].id === scope.editorAPI.homePage.get()
        : null,
  });

  errorReporter(error);

  reportMigrationNotPossible(scope, {
    flow,
    origin,
    isTimeLimitation: isTimeLimitError(error),
    hasPartialMigrationIssue: isPartialMigrationError(error),
    isCancelledByUser: isMigrationCancelled(error),
    isSaveIssue: isSaveError(error),
    hasMigrationIssue: true,
    pagesRefs,
  });

  const hasSectionsAfterRevert = hasPagesWithSectionsInRoot(
    documentServices,
    pagesRefs,
  );

  const hasRevertIssue =
    undoLabelBeforeRevert !== BEFORE_SECTIONS_MIGRATION_HISTORY_LABEL ||
    undoLabelAfterRevert !== initialHistoryLabel ||
    (flow !== MigrationFlow.Editor2Fix &&
      flow !== MigrationFlow.Editor2Anchors &&
      hasSectionsAfterRevert);

  if (hasRevertIssue) {
    scope.editorAPI.bi.event(
      events.sectionsMigration.MIGRATION_FALLBACK_ERROR,
      {
        revision: documentServices.generalInfo.getRevision(),
        flow,
        origin,
        undo_label: undoLabelBeforeRevert,
        sections_number: documentServices.components.get.byType(
          constants.COMP_TYPES.SECTION,
        ).length,
      },
    );
  }

  if (
    flow === MigrationFlow.ADI &&
    (getADIMigrationTrigger() === ADIMigrationTrigger.OnLoad ||
      getADIMigrationTrigger() === ADIMigrationTrigger.Reminder)
  ) {
    await restoreToADI(scope, {
      isCanceledByUser: isMigrationCancelled(error),
    });
  }

  if (
    ((flow === MigrationFlow.Editor || flow === MigrationFlow.HeavySite) &&
      hasRevertIssue) ||
    isSaveError(error)
  ) {
    restoreSession(isPartialMigrationError(error));
  }

  showErrorNotification(scope, { flow, error, origin });

  finishMigration(scope, { flow });
}

function finishMigration(
  scope: Scope,
  {
    flow,
    shouldRenameRevision,
  }: {
    flow: MigrationFlow;
    shouldRenameRevision?: boolean;
  },
) {
  const { editorAPI, migrationStatus } = scope;
  const { autosaveManager, documentServices, savePublish } = editorAPI;

  if (isForceSectionsMigration()) {
    removeForceSectionsMigrationQuery();
  }

  autosaveManager.init({ enabled: true }, true);
  savePublish.unlockSavePublish();

  const shouldRunBackgroundSave = flow !== MigrationFlow.Template;

  if (shouldRunBackgroundSave) {
    saveInBackground(scope, () => {
      if (shouldRenameRevision) {
        renameRevision(
          scope,
          'FTE_Tour_partners_update_site_history_post_update_label',
        );
      }
    });
  }

  documentServices.documentMode.setShouldKeepChildrenInPlace(true);
  documentServices.documentMode.enableShouldUpdateJsonFromMeasureMap(true);

  if (flow === MigrationFlow.Template) {
    cleanupEditorData(documentServices);
    coreUtils.componentSourceFeatureUtils.updateAllChildrenSourceData(
      editorAPI,
      { source: 'template', changedOverride: false },
    );
  }

  migrationStatus.setMigrationFinished();
}
